import { Controller } from '@hotwired/stimulus';

/**
 * Plan Pin Controller (CR remarque « Localisation sur plan » — T524)
 *
 * Lets an architect place a single remarque (observation) pin on a chantier plan
 * for a CR (compte rendu). The affordance is split in two: a COMPACT block inside
 * the remarque slide-over drawer (empty ↔ placed ↔ stale states + 4 hidden pin
 * inputs) and a dedicated FULL-SCREEN modal hosting the plan chips + the big
 * click-to-place canvas. Both surfaces live inside this controller's root (the
 * `RemarkFormComponent` root), so the controller drives both.
 *
 * The plan is chosen from `Ui::FilterPillsComponent` chips (client-only — they
 * emit NO form field). The chosen plan is rendered into the modal (an image via
 * `<img>`, or a PDF's first page via pdfjs-dist fit-to-width into a canvas), and
 * ONE click drops a pin. The click fills the hidden inputs (`observation[plan_page]`
 * / `[plan_x_pct]` / `[plan_y_pct]`); the chip choice fills
 * `observation[plan_document_id]`. There is exactly ONE pin per remarque — a new
 * click replaces it.
 *
 * Two save modes (§1 of tasks/cr-524-plan.md):
 * - CREATE (no persisted Observation): « Enregistrer la position » just flips the
 *   compact block to the placed state client-side and closes the modal; the pin
 *   rides the create form's hidden inputs and is persisted on the create submit.
 * - EDIT (persisted Observation): « Enregistrer la position » does an immediate
 *   CSRF `fetch` PATCH to `localisationUrl`, `renderStreamMessage`s the returned
 *   turbo_stream.replace of ONLY the compact block, then closes the modal. The
 *   CSRF + renderStreamMessage mechanics mirror `cr_inline_field_controller.js`.
 *
 * A read-only overlay mode (`readonly` value) renders the chosen plan with its
 * already-persisted pin and wires no click handler. It is kept for uniformity —
 * the diffused-document overlay is a separate pure-Ruby surface and does NOT use
 * this controller.
 *
 * Targets:
 * - chip: the native plan <select> (F06). Its `change` is caught via
 *   `change->plan-pin#choosePlan`; the controller also syncs its value on modal
 *   reopen / create-mode removePin so the visible selection tracks the pin state.
 * - planDocInput: hidden input `observation[plan_document_id]` (compact block).
 * - emptyState / placedState: the two compact-block states (one carries `hidden`).
 * - placedLabel: the placed-state pastille span (create-mode text is set here).
 * - viewer: the modal container the plan + pin layer render INTO.
 * - empty: the modal's pick-a-plan placeholder (shown until a plan is chosen).
 * - pageInput: hidden input `observation[plan_page]`.
 * - xInput: hidden input `observation[plan_x_pct]`.
 * - yInput: hidden input `observation[plan_y_pct]`.
 * - pageIndicator: modal text element describing the rendered plan.
 * - saveButton: « Enregistrer la position » — server-rendered DISABLED; enabled
 *   only once a pin is actually placed (x/y set). A pin isn't a pin without
 *   coordinates (#524): selecting a plan alone never arms the save.
 * - error: the modal's server-rendered (hidden) danger banner, revealed only when an
 *   edit-mode localisation PATCH fails so the modal stays open (F3).
 *
 * The modal is NOT authoritative over the drawer's hidden inputs by default: it
 * mutates them in place, so « Annuler »/close must UNDO those mutations. A committed
 * snapshot (`committedPin`) is captured at connect (blank in create, persisted in edit)
 * and re-captured on every open + confirmed save/remove; `onModalClose` restores it, so
 * a cancelled placement/plan-switch never leaks into a create submit or an edit save.
 *
 * Values:
 * - plans (Array): `[{ id, url, kind: 'pdf'|'image', indice, label,
 *   replaced_by_indice }, ...]`.
 * - readonly (Boolean): overlay mode — render existing pin, no click-to-place.
 * - workerUrl (String): bundled pdf.worker asset path (`asset_path('pdf.worker.js')`).
 * - mode (String): "create" | "edit" — drives savePosition/removePin.
 * - localisationUrl (String): the edit-mode immediate-save PATCH endpoint.
 * - modalId (String): the dedicated pin-save modal id (window.openModal/closeModal).
 *
 * Actions:
 * - openModal: opens the full-screen modal (compact-block « Situer »/« Modifier »).
 * - choosePlan: `change->plan-pin#choosePlan` — render the plan picked in the <select>.
 * - onModalOpen: `modal:open->plan-pin#onModalOpen` — render on the modal's open
 *   event, AFTER modal_controller un-hides + reflows the modal (fit-to-width fix);
 *   also commits the on-open pin baseline.
 * - onModalClose: `modal:close->plan-pin#onModalClose` — restore the committed pin
 *   baseline (undo an un-saved placement/plan-switch) + clear the error banner.
 * - savePosition: « Enregistrer la position » — create=flip block+commit / edit=CSRF
 *   PATCH; closes ONLY on a confirmed save (else keeps the modal open + shows error).
 * - removePin: « Retirer » — create=reset block / edit=CSRF PATCH (blank plan id);
 *   resets + re-baselines only on a confirmed clear.
 *
 * Events:
 * - plan-pin:placed — dispatched after a click places the pin ({ page, xPct, yPct }).
 */
export default class extends Controller {
  static targets = [
    'chip',
    'planDocInput',
    'emptyState',
    'placedState',
    'placedLabel',
    'viewer',
    'empty',
    'pageInput',
    'xInput',
    'yInput',
    'pageIndicator',
    'saveButton',
    'error',
  ];

  static values = {
    plans: Array,
    readonly: Boolean,
    workerUrl: String,
    mode: String,
    localisationUrl: String,
    modalId: String,
  };

  async connect() {
    this.pin = null;
    this.pdf = null;
    this.pdfjs = null;
    this.currentPlan = null;
    // Tracks which plan the current pin (draft or persisted) belongs to, so a
    // plan switch invalidates the previous pin instead of mis-placing it.
    this.pinnedPlanId = null;
    // The plan the chips have selected. null → #selectedPlan falls back to the
    // persisted plan_document_id hidden input.
    this.selectedPlanId = null;
    // rAF handle for the hidden-modal fit-to-width fallback (cancelled on
    // chip-select / disconnect so a deferred render never double-paints).
    this.openRaf = null;

    // The committed pin baseline « Annuler » restores to (#524). Captured at CONNECT
    // from the server-rendered hidden inputs (blank in create, the persisted pin in
    // edit), NOT null — so a cancel that happens BEFORE the first onModalOpen commit
    // (the create-mode no-plan early-return path) still restores. Re-captured on every
    // modal open and on every successful save/remove; restored on modal:close.
    this.committedPin = this.readonlyValue ? null : this.#snapshotPin();

    if (this.readonlyValue) {
      // Overlay mode (unused by the CR drawer today; kept for uniformity): the
      // plan renders inline, not in a hidden modal, so a connect-time render is
      // safe here.
      await this.#connectReadonly();
      return;
    }

    // Editable mode: the click-to-place canvas lives in a HIDDEN modal, so DON'T
    // render on connect — clientWidth would be 0 → a PDF renders at the wrong
    // (native) scale (§3). The render is deferred to onModalOpen(), fired after
    // modal_controller un-hides the modal and forces a reflow.
    if (this.hasViewerTarget) {
      this.viewerTarget.addEventListener('click', this.#onViewerClick);
    }
  }

  disconnect() {
    this.#cancelOpenRaf();
    if (this.hasViewerTarget) {
      this.viewerTarget.removeEventListener('click', this.#onViewerClick);
    }
    this.#clearViewer();
    // Free the PDFDocumentProxy + its worker transport (fire-and-forget — disconnect
    // is sync); leaving it null'd without destroy leaked the worker across Turbo visits.
    this.pdf?.destroy?.();
    this.pdf = null;
    this.pdfjs = null;
    this.currentPlan = null;
  }

  // ── public actions ───────────────────────────────────────

  // Open the full-screen pin modal (compact-block « Situer sur le plan » /
  // « Modifier »). These buttons sit inside the drawer <form>, so preventDefault
  // guards against a stray submit even though they carry type="button".
  openModal(event) {
    event?.preventDefault();
    if (this.hasModalIdValue && window.openModal) {
      window.openModal(this.modalIdValue);
    }
  }

  // Plan select (native <select> `change`, F06). Renders the newly-picked plan into
  // the already-visible modal (width > 0 → correct fit-to-width). Selecting a plan
  // does NOT arm the pin: planDocInput + the save button are (re)synced from the
  // actual placement state (#524) — a coordinate-less plan is never « placed ».
  choosePlan(event) {
    // A plan select supersedes any pending open-render.
    this.#cancelOpenRaf();

    // The native <select> `change` carries the value on event.target.value; the legacy
    // filter-pills CustomEvent carried it on event.detail.value — read either (F06).
    const value = event?.target?.value ?? event?.detail?.value;
    const plan = this.#planById(value);

    if (!plan) {
      this.selectedPlanId = null;
      this.#clearViewer();
      this.#clearPinInputs();
      this.pinnedPlanId = null;
      this.currentPlan = null;
      this.#resetPageIndicator();
      this.#showEmpty();
      this.#syncPlacement(); // no plan → clear planDocInput + disable save
      return;
    }

    this.selectedPlanId = plan.id;

    // Switching to a DIFFERENT plan invalidates the previous pin — the persisted
    // coordinates only make sense against the plan they were placed on.
    if (this.pinnedPlanId != null && String(this.pinnedPlanId) !== String(plan.id)) {
      this.#clearPinInputs();
      this.pinnedPlanId = null;
    }

    // Re-selecting the ALREADY-rendered plan: skip the expensive re-fetch/re-rasterize
    // (pdfjs.getDocument + full re-render) — nothing changed, just re-sync placement.
    if (this.currentPlan && String(this.currentPlan.id) === String(plan.id)) {
      this.#syncPlacement();
      return;
    }

    this.#renderPlan(plan);
    // Reflect the post-switch placement: planDocInput carries the plan id (and the
    // save button arms) ONLY when a pin for THIS plan survived the switch.
    this.#syncPlacement();
  }

  // Modal open event (non-bubbling `modal:open`, dispatched on the modal element
  // by modal_controller#open AFTER it removes `.hidden` and forces a reflow — so
  // the viewer has a real clientWidth here, which is the whole point of rendering
  // on open, not connect, §3).
  onModalOpen() {
    this.#cancelOpenRaf();
    this.#clearModalError();

    const plan = this.#selectedPlan();
    // Keep the native plan <select> in lock-step with the (possibly restored) pin
    // state, so a reopen after a cancelled switch shows the committed plan (F06).
    this.#syncChip(plan?.id ?? '');
    if (!plan) {
      // No plan chosen yet — show the pick-a-plan placeholder. (No commit here: the
      // CONNECT-time baseline already holds the blank create-mode state, so a cancel
      // from this path still restores blank — #524.)
      this.#clearViewer();
      this.#resetPageIndicator();
      this.#showEmpty();
      this.#disableSaveButton();
      return;
    }

    // Reopening an edited remarque: the persisted coords belong to the pinned
    // plan, so seed pinnedPlanId → #renderPlan's #drawExistingPinIfAny redraws
    // the pin at the stored coordinates.
    if (this.#hasPersistedCoords()) this.pinnedPlanId = plan.id;

    // Arm the save button when reopening a COMPLETE pre-existing pin; keep it
    // disabled for a plan selected without coordinates (#524).
    this.#syncPlacement();

    // Re-capture the baseline « Annuler » restores to = the state as of THIS open
    // (persisted/last-saved pin), so any in-modal fiddling is undone on cancel.
    this.#commitSnapshot();

    // Belt-and-suspenders: if a nested layout still reports 0 width, defer ONE
    // frame so #fitWidthScale reads a real width rather than the 1.0 fallback.
    if (this.hasViewerTarget && this.viewerTarget.clientWidth === 0) {
      this.openRaf = requestAnimationFrame(() => {
        this.openRaf = null;
        this.#renderPlan(plan);
      });
      return;
    }

    this.#renderPlan(plan);
  }

  // « Enregistrer la position ». CREATE: the hidden inputs already carry the pin
  // (choosePlan wrote plan_document_id; #onViewerClick wrote page/x/y) — reflect
  // it in the compact block client-side, no server round-trip. EDIT: immediate
  // CSRF PATCH to the localisation endpoint (mirrors cr-inline-field).
  async savePosition(event) {
    event?.preventDefault();

    // A pin isn't a pin without coordinates (#524). The disabled save button
    // already prevents this, but guard the programmatic path too — a selected-but-
    // unclicked plan must never save a coordinate-less plan_document_id.
    if (!this.#hasPersistedCoords()) return;

    if (this.modeValue === 'create') {
      if (this.#hasSelectedPlan()) this.#applyPlacedStateClientSide();
      // Adopt the just-placed pin as the committed baseline BEFORE closing, so the
      // ensuing modal:close restore is a no-op (the pin rides the create submit).
      this.#commitSnapshot();
      this.#closeModal();
      return;
    }

    // EDIT: only close on a CONFIRMED save. A failed PATCH (302/500/network — or a
    // 422 that still renders its error stream) keeps the modal OPEN with the placed
    // pin, surfaces the inline error, and does NOT commit — so « Annuler » restores
    // the previously-persisted pin, never the unsaved coords.
    const ok = await this.#patchLocalisation(this.#pinFormData());
    if (!ok) {
      this.#showModalError();
      return;
    }
    this.#clearModalError();
    this.#commitSnapshot();
    this.#closeModal();
  }

  // « Retirer ». CREATE: clear the hidden inputs + reset the compact block to the
  // empty state. EDIT: CSRF PATCH with a BLANK plan_document_id → the server
  // unpins (PlanPin.clear!) and re-renders the empty compact block.
  async removePin(event) {
    event?.preventDefault();

    if (this.modeValue === 'create') {
      this.#clearPinInputs();
      if (this.hasPlanDocInputTarget) this.planDocInputTarget.value = '';
      this.selectedPlanId = null;
      this.pinnedPlanId = null;
      this.#syncChip('');
      this.#showEmptyState();
      this.#disableSaveButton();
      // Commit the now-empty state so a later modal cancel can't resurrect the pin.
      this.#commitSnapshot();
      return;
    }

    // EDIT: unpin server-side; only reset the in-memory pin state (and re-baseline)
    // on a CONFIRMED clear. The compact block is re-rendered empty by the stream.
    const ok = await this.#patchLocalisation(this.#pinFormData({ blank: true }));
    if (!ok) return;
    this.selectedPlanId = null;
    this.pinnedPlanId = null;
    this.#commitSnapshot();
  }

  // Restore the drawer's hidden pin inputs to the on-open / last-saved baseline when
  // the modal closes without a confirmed save (« Annuler », backdrop, Escape). Without
  // this, a cancelled placement/plan-switch leaks into the create submit (phantom pin)
  // or, via the drawer form, a later edit save (#524). Wired `modal:close->…#onModalClose`.
  onModalClose() {
    this.#clearModalError();
    this.#cancelOpenRaf();
    if (this.committedPin) this.#restorePin(this.committedPin);
  }

  // ── connect helpers ──────────────────────────────────────

  async #connectReadonly() {
    const plan = this.#selectedPlan();
    if (!plan) {
      this.#showEmpty();
      return;
    }
    // In overlay mode the persisted pin belongs to the shown plan by construction.
    this.pinnedPlanId = plan.id;
    await this.#renderPlan(plan);
  }

  // ── edit-mode immediate save (CSRF fetch + Turbo Stream) ──

  // PATCH the pin quartet to the localisation endpoint. The response is
  // discriminated on Content-Type (NOT response.ok) — a 422 carrying the compact
  // block re-rendered WITH errors is a legitimate turbo stream that MUST still
  // render; a 302→login / 500 error page is NOT a stream and is logged, never
  // fed to renderStreamMessage. Faithful calque of cr_inline_field_controller.js.
  async #patchLocalisation(formData) {
    const url = this.hasLocalisationUrlValue ? this.localisationUrlValue : '';
    if (!url) return false;

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        body: formData,
        credentials: 'same-origin',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': this.#csrfToken(),
        },
      });

      // Always paint a turbo-stream body — a 422 re-renders the compact block WITH
      // errors and must still render. But SUCCESS is HTTP-2xx (`response.ok`), NOT
      // merely "was a stream": a 422 must read as a FAILURE so the caller keeps the
      // modal open (and retains the placed pin) instead of dismissing as if saved.
      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('text/vnd.turbo-stream.html')) {
        const body = await response.text();
        window.Turbo?.renderStreamMessage?.(body);
      }
      if (!response.ok) {
        console.error(
          `[plan-pin] localisation PATCH failed: HTTP ${response.status} (${contentType})`
        );
      }
      return response.ok;
    } catch (error) {
      console.error('[plan-pin] localisation PATCH failed:', error);
      return false;
    }
  }

  // The nested FormData the localisation endpoint expects (mirrors the drawer form's
  // `observation[...]` field names). `{ blank: true }` sends an all-empty quartet — a
  // blank plan_document_id tells the server to clear the pin (« Retirer »).
  #pinFormData({ blank = false } = {}) {
    const read = (target, hasTarget) => (blank ? '' : this.#inputValue(target, hasTarget));
    const formData = new FormData();
    formData.append(
      'observation[plan_document_id]',
      read(this.planDocInputTarget, this.hasPlanDocInputTarget)
    );
    formData.append('observation[plan_page]', read(this.pageInputTarget, this.hasPageInputTarget));
    formData.append('observation[plan_x_pct]', read(this.xInputTarget, this.hasXInputTarget));
    formData.append('observation[plan_y_pct]', read(this.yInputTarget, this.hasYInputTarget));
    return formData;
  }

  #inputValue(target, hasTarget) {
    return hasTarget && target ? target.value : '';
  }

  // ── committed-vs-draft pin snapshot (« Annuler » authority, #524) ─────────

  // Read the current pin state (the 4 hidden inputs + the two in-memory plan ids).
  #snapshotPin() {
    return {
      planDocId: this.#inputValue(this.planDocInputTarget, this.hasPlanDocInputTarget),
      page: this.#inputValue(this.pageInputTarget, this.hasPageInputTarget),
      x: this.#inputValue(this.xInputTarget, this.hasXInputTarget),
      y: this.#inputValue(this.yInputTarget, this.hasYInputTarget),
      selectedPlanId: this.selectedPlanId,
      pinnedPlanId: this.pinnedPlanId,
    };
  }

  // Adopt the CURRENT pin state as the committed baseline (on open + on every
  // confirmed save/remove) — so a later modal:close restore is a no-op.
  #commitSnapshot() {
    this.committedPin = this.#snapshotPin();
  }

  // Restore the drawer's hidden inputs + the two plan ids to a snapshot. The pin
  // redraw is deferred to the next onModalOpen (which re-renders from #selectedPlan).
  #restorePin(snap) {
    if (!snap) return;
    if (this.hasPlanDocInputTarget) this.planDocInputTarget.value = snap.planDocId || '';
    if (this.hasPageInputTarget) this.pageInputTarget.value = snap.page || '';
    if (this.hasXInputTarget) this.xInputTarget.value = snap.x || '';
    if (this.hasYInputTarget) this.yInputTarget.value = snap.y || '';
    this.selectedPlanId = snap.selectedPlanId ?? null;
    this.pinnedPlanId = snap.pinnedPlanId ?? null;
  }

  // Reveal / hide the modal's server-rendered (hidden) danger banner. Toggles the
  // `hidden` class only — the colour is authored by AlertComponent (design contract).
  #showModalError() {
    if (this.hasErrorTarget) this.errorTarget.classList.remove('hidden');
  }

  #clearModalError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add('hidden');
  }

  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || '';
  }

  #closeModal() {
    if (this.hasModalIdValue && window.closeModal) {
      window.closeModal(this.modalIdValue);
    }
  }

  // ── compact-block client-side state (create mode) ─────────

  // Reflect the just-placed pin in the compact block WITHOUT a server round-trip.
  // Sets the pastille TEXT ONLY — no colors/markup authored in JS (DESIGN
  // contract). The green « Repère posé » badge is server-rendered and always in
  // the placed-state DOM (initially `hidden`); flipping the states reveals it.
  #applyPlacedStateClientSide() {
    if (this.hasPlacedLabelTarget) {
      this.placedLabelTarget.textContent = this.#placedLabelText();
    }
    if (this.hasPlacedStateTarget) this.placedStateTarget.classList.remove('hidden');
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add('hidden');
  }

  // Reset the compact block to its empty state (create-mode « Retirer »).
  #showEmptyState() {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove('hidden');
    if (this.hasPlacedStateTarget) this.placedStateTarget.classList.add('hidden');
  }

  // « <label> · Indice <X> » from the selected plan row (mirrors the server
  // pastille shape). Text only.
  #placedLabelText() {
    const plan = this.#selectedPlan();
    if (!plan) return '';
    return [plan.label, plan.indice ? `Indice ${plan.indice}` : null].filter(Boolean).join(' · ');
  }

  // ── placement state (save-button + create-form plan id) ───

  // A pin is « placed » ONLY with coordinates on a known pinned plan (#524).
  // Reflects that state: planDocInput carries the plan id (so a CREATE submit
  // sends it) ONLY when placed, and the save button arms/disarms in lock-step.
  #syncPlacement() {
    const placed = this.pinnedPlanId != null && this.#hasPersistedCoords();
    if (this.hasPlanDocInputTarget) {
      this.planDocInputTarget.value = placed ? String(this.pinnedPlanId) : '';
    }
    if (placed) this.#enableSaveButton();
    else this.#disableSaveButton();
  }

  #enableSaveButton() {
    if (!this.hasSaveButtonTarget) return;
    this.saveButtonTarget.disabled = false;
    this.saveButtonTarget.setAttribute('aria-disabled', 'false');
  }

  #disableSaveButton() {
    if (!this.hasSaveButtonTarget) return;
    this.saveButtonTarget.disabled = true;
    this.saveButtonTarget.setAttribute('aria-disabled', 'true');
  }

  // ── rendering ────────────────────────────────────────────

  async #renderPlan(plan) {
    if (!this.hasViewerTarget) return;

    this.currentPlan = plan;
    this.#hideEmpty();
    this.#clearViewer();
    // Free the previous PDFDocumentProxy + worker before dropping the ref, else each
    // plan switch/reopen orphans a worker transport (leak).
    await this.pdf?.destroy();
    this.pdf = null;
    this.#setPageIndicator(plan);

    try {
      if (plan.kind === 'pdf') {
        await this.#renderPdf(plan);
      } else {
        this.#renderImage(plan);
      }
    } catch (error) {
      console.error('plan-pin: plan render failed', error);
      this.#showError('Aperçu du plan indisponible.');
      return;
    }

    // Only redraw the persisted/draft pin if it actually belongs to this plan.
    if (this.pinnedPlanId != null && String(this.pinnedPlanId) === String(plan.id)) {
      this.#drawExistingPinIfAny();
    }
  }

  #renderImage(plan) {
    const wrapper = this.#buildWrapper('w-fit max-w-full');

    const img = document.createElement('img');
    img.src = plan.url;
    img.alt = plan.label || 'Plan';
    img.className = 'block max-w-full h-auto select-none';
    img.draggable = false;
    img.dataset.page = '1';
    if (!this.readonlyValue) img.style.cursor = 'crosshair';

    wrapper.appendChild(img);
    wrapper.appendChild(this.#buildPinLayer());
  }

  async #renderPdf(plan) {
    const pdfjs = await this.#loadPdfjs();
    if (!pdfjs) {
      this.#showError('Aperçu PDF indisponible.');
      return;
    }

    this.pdf = await pdfjs.getDocument(plan.url).promise;
    const page = await this.pdf.getPage(1);
    const scale = this.#fitWidthScale(page);
    const viewport = page.getViewport({ scale });

    // Wrapper MUST be position: relative (from #buildWrapper) so the pin layer's
    // `inset-0` is scoped to the page and % pin placement resolves correctly.
    const wrapper = this.#buildWrapper();
    wrapper.style.width = `${viewport.width}px`;
    wrapper.style.height = `${viewport.height}px`;

    const canvas = document.createElement('canvas');
    canvas.dataset.page = '1';
    canvas.className = 'block';
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    if (!this.readonlyValue) canvas.style.cursor = 'crosshair';

    wrapper.appendChild(canvas);
    wrapper.appendChild(this.#buildPinLayer());

    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
  }

  async #loadPdfjs() {
    if (this.pdfjs) return this.pdfjs;
    const pdfjs = await import('pdfjs-dist');
    if (this.workerUrlValue) {
      pdfjs.GlobalWorkerOptions.workerSrc = this.workerUrlValue;
    } else {
      // No worker asset wired — pdfjs v4 needs one. Fail soft rather than throw.
      console.error('plan-pin: missing workerUrl value — PDF plans cannot render.');
      return null;
    }
    this.pdfjs = pdfjs;
    return pdfjs;
  }

  // Fit-to-width: scale the PDF page so it matches the viewer's current width.
  // Falls back to 1.0 when the viewer is hidden (clientWidth = 0).
  #fitWidthScale(page) {
    const FALLBACK_SCALE = 1.0;
    if (!this.hasViewerTarget) return FALLBACK_SCALE;
    const width = this.viewerTarget.clientWidth;
    if (width <= 0) return FALLBACK_SCALE;
    const naturalWidth = page.getViewport({ scale: 1 }).width;
    if (!naturalWidth) return FALLBACK_SCALE;
    return width / naturalWidth;
  }

  #buildWrapper(extraClass = '') {
    const wrapper = document.createElement('div');
    wrapper.dataset.role = 'plan-surface';
    wrapper.className =
      `relative mx-auto bg-white shadow-sm rounded-lg overflow-hidden ${extraClass}`.trim();
    this.viewerTarget.appendChild(wrapper);
    return wrapper;
  }

  #buildPinLayer() {
    const layer = document.createElement('div');
    layer.dataset.role = 'pin-layer';
    layer.className = 'absolute inset-0 pointer-events-none';
    return layer;
  }

  // ── pin ──────────────────────────────────────────────────

  // Delegated click on the viewer. The pin layer is pointer-events-none, so a
  // click "on" the pin passes through to the canvas/img beneath — event.target
  // is therefore always the surface (which carries data-page).
  #onViewerClick = (event) => {
    if (this.readonlyValue || !this.currentPlan) return;

    const surface = event.target.closest('[data-page]');
    if (!surface) return;

    const rect = surface.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const page = Number(surface.dataset.page) || 1;
    const xPct = ((event.clientX - rect.left) / rect.width) * 100;
    const yPct = ((event.clientY - rect.top) / rect.height) * 100;

    if (this.hasPageInputTarget) this.pageInputTarget.value = String(page);
    if (this.hasXInputTarget) this.xInputTarget.value = xPct.toFixed(2);
    if (this.hasYInputTarget) this.yInputTarget.value = yPct.toFixed(2);

    this.pinnedPlanId = this.currentPlan.id;
    this.#drawPin(page, xPct, yPct);
    // Placement is what arms the pin: write planDocInput (so a CREATE submit carries
    // the plan id) + enable the save button — never on mere plan selection (#524).
    this.#syncPlacement();
    this.dispatch('placed', { detail: { page, xPct, yPct } });
  };

  // Draw the persisted pin from the hidden inputs, if present and on the single
  // rendered page (page 1). Coordinates are preserved even when skipped.
  #drawExistingPinIfAny() {
    if (!this.hasXInputTarget || !this.hasYInputTarget) return;
    const rawX = this.xInputTarget.value?.trim();
    const rawY = this.yInputTarget.value?.trim();
    if (!rawX || !rawY) return;

    const x = Number(rawX);
    const y = Number(rawY);
    if (Number.isNaN(x) || Number.isNaN(y)) return;

    const page = this.hasPageInputTarget ? Number(this.pageInputTarget.value) || 1 : 1;
    if (page !== 1) return; // only page 1 is rendered in this iteration

    this.#drawPin(page, x, y);
  }

  #drawPin(page, xPct, yPct) {
    const layer = this.#pinLayer();
    if (!layer) return;

    this.#clearPin();

    // Clamp visual placement to [2, 98] so a pin at an exact 0/100 boundary
    // isn't half-clipped (the pin is -translate-x/y-1/2). Persisted coord is
    // unchanged — only the on-screen dot is clamped.
    const x = Math.min(98, Math.max(2, Number(xPct)));
    const y = Math.min(98, Math.max(2, Number(yPct)));

    const pin = document.createElement('div');
    pin.dataset.planPin = '';
    pin.dataset.page = String(page);
    pin.className =
      'absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-danger-600 text-white text-xs font-semibold flex items-center justify-center shadow ring-2 ring-white/70';
    pin.style.left = `${x}%`;
    pin.style.top = `${y}%`;
    // Keep the pin non-interactive so subsequent clicks re-place it via the
    // surface beneath rather than being swallowed by the pin.
    pin.style.pointerEvents = 'none';

    layer.appendChild(pin);
    this.pin = pin;
  }

  #clearPin() {
    if (this.pin) {
      this.pin.remove();
      this.pin = null;
    }
    // Belt-and-suspenders: only ONE pin per remarque, so wipe any stray.
    this.#pinLayer()?.replaceChildren();
  }

  #clearPinInputs() {
    if (this.hasPageInputTarget) this.pageInputTarget.value = '';
    if (this.hasXInputTarget) this.xInputTarget.value = '';
    if (this.hasYInputTarget) this.yInputTarget.value = '';
    this.#clearPin();
  }

  #pinLayer() {
    return this.hasViewerTarget ? this.viewerTarget.querySelector('[data-role="pin-layer"]') : null;
  }

  #hasPersistedCoords() {
    if (!this.hasXInputTarget || !this.hasYInputTarget) return false;
    return Boolean(this.xInputTarget.value?.trim()) && Boolean(this.yInputTarget.value?.trim());
  }

  // ── viewer chrome ────────────────────────────────────────

  // Remove only our own rendered surfaces / error banner — never the `empty`
  // placeholder (which is a sibling of the viewer and is toggled, not deleted).
  #clearViewer() {
    if (!this.hasViewerTarget) return;
    this.viewerTarget
      .querySelectorAll('[data-role="plan-surface"], [data-role="plan-error"]')
      .forEach((el) => el.remove());
    this.pin = null;
  }

  #showError(message) {
    if (!this.hasViewerTarget) return;
    this.#clearViewer();
    const banner = document.createElement('div');
    banner.dataset.role = 'plan-error';
    banner.className =
      'px-3 py-2 text-sm text-danger-700 bg-danger-50 border border-danger-200 rounded-md';
    banner.textContent = message;
    this.viewerTarget.appendChild(banner);
  }

  #showEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.classList.remove('hidden');
  }

  #hideEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.classList.add('hidden');
  }

  #setPageIndicator(plan) {
    if (!this.hasPageIndicatorTarget) return;
    const parts = [plan.label, plan.indice ? `Indice ${plan.indice}` : null].filter(Boolean);
    this.pageIndicatorTarget.textContent = parts.join(' · ');
  }

  #resetPageIndicator() {
    if (this.hasPageIndicatorTarget) this.pageIndicatorTarget.textContent = '';
  }

  #cancelOpenRaf() {
    if (this.openRaf) {
      cancelAnimationFrame(this.openRaf);
      this.openRaf = null;
    }
  }

  // ── plan lookup ──────────────────────────────────────────

  // The selected plan: the chip choice wins; else the persisted
  // plan_document_id hidden input; else the first plan (defensive).
  #selectedPlan() {
    const id =
      this.selectedPlanId ??
      (this.hasPlanDocInputTarget ? this.planDocInputTarget.value : this.plansValue[0]?.id);
    return this.#planById(id);
  }

  #hasSelectedPlan() {
    return Boolean(this.#selectedPlan());
  }

  #planById(id) {
    if (id === '' || id == null) return null;
    return this.plansValue.find((plan) => String(plan.id) === String(id)) || null;
  }

  // Keep the native plan <select> (`chip` target) in sync with a programmatic
  // selection change (create-mode removePin, modal reopen). No-op for a missing or
  // non-<select> chip target (defensive).
  #syncChip(value) {
    if (!this.hasChipTarget || this.chipTarget.tagName !== 'SELECT') return;
    this.chipTarget.value = value == null ? '' : String(value);
  }
}
